/*
 * Copyright 2025 coze-dev Authors
 * Modifications Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Component, type ReactNode } from 'react';

import { CDLocaleProvider, zh_CN, en_US } from '@coze-arch/coze-design/locales';

import { type Intl } from '../intl';
import { i18nContext, type I18nContext } from './context';

export { i18nContext, type I18nContext };

// Helper to get locale config for UI component libraries
// Always return a valid locale object
const getUILocaleFromLanguage = (language: string) => {
  // Ensure we always have valid locale objects
  const safeZhCN = zh_CN || { code: 'zh-CN' };
  const safeEnUS = en_US || { code: 'en-US' };
  const safeZhTW = safeZhCN;
  
  switch (language) {
    case 'en':
    case 'en-US':
      return safeEnUS;
    case 'zh-HK':
      return safeZhTW;
    case 'zh-CN':
    default:
      return safeZhCN;
  }
};

export interface I18nProviderProps {
  children?: ReactNode;
  i18n: Intl;
}

export class I18nProvider extends Component<I18nProviderProps> {
  constructor(props: I18nProviderProps) {
    super(props);
    this.state = {};
  }

  render() {
    const {
      children,
      i18n = {
        t: (k: string) => k,
        language: 'zh-CN',
      },
    } = this.props;
    
    // Get the locale config based on i18n language
    const locale = getUILocaleFromLanguage(i18n.language || 'zh-CN');
    
    // Pass both locale and i18n to satisfy CDLocaleProvider requirements
    return (
      <CDLocaleProvider locale={locale} i18n={i18n}>
        <i18nContext.Provider value={{ i18n: i18n as Intl }}>
          {children}
        </i18nContext.Provider>
      </CDLocaleProvider>
    );
  }
}
